/* eslint-disable react-refresh/only-export-components */
import { useTheme } from '@mui/material';
import {
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonStrikethrough,
  MenuButtonEditLink,
  MenuSelectTextAlign,
  MenuButtonOrderedList,
  MenuButtonBulletedList,
  isTouchDevice,
  MenuButtonIndent,
  MenuButtonUnindent,
  MenuButtonBlockquote,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonHorizontalRule,
  MenuButtonRemoveFormatting,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonUnderline,
  MenuButtonSuperscript,
  MenuButtonSubscript,
  MenuSelectFontSize,
  MenuButtonTaskList,
  MenuButtonAddTable,
  MenuButtonImageUpload,
  MenuButtonHighlightColor,
  MenuButtonTextColor,
  MenuSelectFontFamily,
} from 'mui-tiptap';

type ControllerOption = { label: string; value: string };
type UploadedImage = { src: string; alt: string };

const mapFilesToObjectUrls = (files: File[]): UploadedImage[] =>
  files.map((file) => ({
    src: URL.createObjectURL(file),
    alt: file.name,
  }));

const convertFileToBase64 = (file: File): Promise<UploadedImage> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({ src: reader.result as string, alt: file.name });
    };
    reader.onerror = (error: unknown) => {
      reject(error instanceof Error ? error : (reader.error ?? new Error('Failed to convert file to base64.')));
    };
    reader.readAsDataURL(file);
  });

const uploadFilesAsBase64 = (files: File[]): Promise<UploadedImage[]> => Promise.all(files.map(convertFileToBase64));

export const FontFamilies: ControllerOption[] = [
  {
    label: 'Default',
    value:
      '"Atlassian Sans", ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif',
  },
  { label: 'Futura', value: 'futura' },
  { label: 'Impact', value: 'Impact' },
  { label: 'Haettenschweiler', value: 'Haettenschweiler' },
  { label: 'Arial Narrow Bold', value: 'Arial Narrow Bold' },
  { label: 'Sans-serif', value: 'sans-serif' },
  { label: 'Whitneybook', value: 'whitneybook' },
];

export const TextColors: ControllerOption[] = [
  { value: '#000000', label: 'Black' },
  { value: '#ffffff', label: 'White' },
  { value: '#414042', label: 'Grey' },
  { value: '#e61432', label: 'Red' },
  { value: '#ff8b2a', label: 'Orange' },
  { value: '#ffff00', label: 'Yellow' },
  { value: '#79d92f', label: 'Green' },
  { value: '#007836', label: 'Dark Green' },
  { value: '#65c0e0', label: 'Blue' },
];

export const HighlightColors: ControllerOption[] = [
  { value: '#7b7b7e', label: 'Dark grey' },
  { value: '#e4e4e4', label: 'Light grey' },
  { value: '#f5a1ad', label: 'Light red' },
  { value: '#ffd0a9', label: 'Light orange' },
  { value: '#fff87f', label: 'Yellow' },
  { value: '#afe883', label: 'Light green' },
  { value: '#98c8ae', label: 'Dark green' },
  { value: '#d3effb', label: 'Light blue' },
  { value: '#dcd7e9', label: 'Light purple' },
];

export const RichTextControls = () => {
  const isMobile = isTouchDevice();
  const theme = useTheme();
  return (
    <MenuControlsContainer>
      {isMobile ? (
        <>
          <MenuSelectFontFamily options={FontFamilies} />
          <MenuDivider />
          <MenuButtonBlockquote />
          <MenuButtonBold />
          <MenuButtonItalic />
          <MenuButtonUnderline />
          <MenuButtonStrikethrough />
          <MenuButtonImageUpload onUploadFiles={mapFilesToObjectUrls} />
          <MenuDivider />
          <MenuButtonUndo />
          <MenuButtonRedo />
          <MenuSelectHeading />
          <MenuButtonSubscript />
          <MenuButtonSuperscript />
          <MenuButtonTextColor defaultTextColor={theme.palette.text.primary} swatchColors={TextColors} />
          <MenuButtonHighlightColor swatchColors={HighlightColors} />
          <MenuButtonEditLink />
          <MenuButtonHorizontalRule />
          <MenuButtonRemoveFormatting />
          <MenuButtonAddTable />
          <MenuSelectFontSize />
          <MenuDivider />
          <MenuSelectTextAlign />
          <MenuDivider />
          <MenuButtonOrderedList />
          <MenuButtonBulletedList />
          <MenuButtonTaskList />
          {isTouchDevice() && (
            <>
              {/* On touch devices, we'll show indent/unindent buttons, since they're
                unlikely to have a keyboard that will allow for using Tab/Shift+Tab. These
                buttons probably aren't necessary for keyboard users and would add extra
                clutter.
              */}
              <MenuButtonIndent />
              <MenuButtonUnindent />
            </>
          )}
          <MenuDivider />
          <MenuButtonCode />
          <MenuButtonCodeBlock />
        </>
      ) : (
        <>
          <MenuButtonUndo />
          <MenuButtonRedo />
          <MenuDivider />
          <MenuButtonBold />
          <MenuButtonItalic />
          <MenuButtonUnderline />
          <MenuButtonStrikethrough />
          <MenuDivider />
          <MenuButtonOrderedList />
          <MenuButtonBulletedList />
          <MenuButtonTaskList />
          <MenuSelectHeading />
          <MenuButtonSubscript />
          <MenuButtonSuperscript />
          <MenuButtonTextColor defaultTextColor={theme.palette.text.primary} swatchColors={TextColors} />
          <MenuButtonHighlightColor swatchColors={HighlightColors} />
          <MenuDivider />
          <MenuButtonEditLink />
          <MenuDivider />
          <MenuButtonBlockquote />
          <MenuSelectTextAlign />
          <MenuDivider />
          {isTouchDevice() && (
            <>
              {/* On touch devices, we'll show indent/unindent buttons, since they're
                unlikely to have a keyboard that will allow for using Tab/Shift+Tab. These
                buttons probably aren't necessary for keyboard users and would add extra
                clutter.
                */}
              <MenuButtonIndent />
              <MenuButtonUnindent />
              <MenuDivider />
            </>
          )}
          <MenuButtonCode />
          <MenuButtonCodeBlock />
          <MenuDivider />
          <MenuButtonImageUpload onUploadFiles={uploadFilesAsBase64} />
          <MenuDivider />
          <MenuButtonHorizontalRule />
          <MenuButtonAddTable />
          <MenuDivider />
          <MenuButtonRemoveFormatting />
          <MenuSelectFontFamily options={FontFamilies} />
          <MenuSelectFontSize />
        </>
      )}
    </MenuControlsContainer>
  );
};

export default RichTextControls;
